import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from '@mui/material';

import {
  MDTypography,
  MDInput,
  MDButton,
  CloseButton
} from '@components/index';
import { ChartDetails } from '@DStypes/chart';
import { RootState } from '@store/store';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  ADDED_CHART_LONG_DESCRIPTION,
  ADDED_CHART_SHORT_DESCRIPTION,
  CHANGED_CHART_NAME
} from '@constants/eventNames';

const ChartDetailsModal = ({
  open,
  onClose,
  onUpdateChartDetails
}: {
  open: boolean;
  onClose: () => void;
  onUpdateChartDetails: (arg: ChartDetails) => void;
}) => {
  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );

  const [errorMessage, setErrorMessage] = useState({
    chart_title: '',
    chart_short_desc: '',
    chart_long_desc: ''
  });

  const [chartDetails, setChartDetails] = useState({
    chart_title: '',
    chart_short_desc: '',
    chart_long_desc: ''
  });

  const isChartNameLimitExceeded = chartDetails.chart_title?.length > 150;

  const isShortDescLimitExceeded = chartDetails.chart_short_desc?.length > 500;

  const isLongDescLimitExceeded = chartDetails.chart_long_desc?.length > 1000;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setChartDetails((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleUpdateChart = () => {
    if (currentChartData.chart_long_desc !== chartDetails.chart_long_desc)
      sendAmplitudeData(ADDED_CHART_LONG_DESCRIPTION);

    if (currentChartData.chart_short_desc !== chartDetails.chart_short_desc)
      sendAmplitudeData(ADDED_CHART_SHORT_DESCRIPTION);

    if (currentChartData.chart_title !== chartDetails.chart_title)
      sendAmplitudeData(CHANGED_CHART_NAME);

    if (chartDetails.chart_title.replace(/\s/g, '').length === 0) {
      setErrorMessage((prevState) => ({
        ...prevState,
        chart_title: 'Please provide a Chart Name'
      }));

      return;
    }

    if (
      !isChartNameLimitExceeded &&
      !isShortDescLimitExceeded &&
      !isLongDescLimitExceeded
    )
      onUpdateChartDetails(chartDetails);
  };

  useEffect(() => {
    setChartDetails({
      chart_title: currentChartData.chart_title,
      chart_short_desc: currentChartData.chart_short_desc,
      chart_long_desc: currentChartData.chart_long_desc
    });
  }, [currentChartData]);

  useEffect(() => {
    if (isChartNameLimitExceeded)
      setErrorMessage((prevState) => ({
        ...prevState,
        chart_title: 'Character limit 150 exceeded'
      }));
    else
      setErrorMessage((prevState) => ({
        ...prevState,
        chart_title: ''
      }));

    if (isShortDescLimitExceeded)
      setErrorMessage((prevState) => ({
        ...prevState,
        chart_short_desc: 'Character limit 500 exceeded'
      }));
    else
      setErrorMessage((prevState) => ({
        ...prevState,
        chart_short_desc: ''
      }));

    if (isLongDescLimitExceeded)
      setErrorMessage((prevState) => ({
        ...prevState,
        chart_long_desc: 'Character limit 1000 exceeded'
      }));
    else
      setErrorMessage((prevState) => ({
        ...prevState,
        chart_long_desc: ''
      }));
  }, [chartDetails]);

  const chartType = currentChartData.config.chart_type;

  return (
    <Dialog
      open={open}
      maxWidth='xs'
      fullWidth
      onClose={onClose}
      sx={{
        zIndex: 9999,
        '& .MuiPaper-root': {
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      <CloseButton onClose={() => onClose()} />
      <DialogTitle sx={{ pl: '20px', my: '10px' }}>
        <MDTypography variant='h5' color='secondary' textGradient>
          {chartType === 'KPI'
            ? 'KPI Name and Description'
            : 'Chart Name and Description'}
        </MDTypography>
      </DialogTitle>
      <DialogContent sx={{ px: '40px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container direction='column' spacing={1}>
              <Grid item>
                <MDTypography variant='h6' color='dark' mb={0.5}>
                  {chartType === 'KPI' ? 'KPI Name' : 'Chart Name'}
                </MDTypography>
                <MDInput
                  fullWidth
                  name='chart_title'
                  placeholder={chartType === 'KPI' ? 'KPI Name' : 'Chart Name'}
                  multiline
                  rows={3}
                  value={chartDetails.chart_title}
                  onChange={handleChange}
                  error={errorMessage?.chart_title.length !== 0}
                  helperText={errorMessage?.chart_title}
                  sx={{ backgroundImage: 'none' }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container direction='column' spacing={1}>
              <Grid item>
                <MDTypography variant='h6' color='dark' mb={0.5}>
                  {chartType === 'KPI'
                    ? 'KPI Short Description (shown in both, downloaded KPI and Report)'
                    : 'Chart Short Description (shown in both, downloaded Chart and Report)'}
                </MDTypography>
                <MDInput
                  name='chart_short_desc'
                  placeholder={
                    chartType === 'KPI'
                      ? 'Give a short description to your KPI eg: This KPI shows total number of clicks on a campaign.'
                      : 'Give a short description to your chart e.g. This chart shows sum of earnings per quarter, grouped by products. AirPods is the highest amongst all.'
                  }
                  fullWidth
                  multiline
                  rows={3}
                  value={chartDetails.chart_short_desc}
                  onChange={handleChange}
                  error={errorMessage?.chart_short_desc.length !== 0}
                  helperText={errorMessage?.chart_short_desc}
                  sx={{ backgroundImage: 'none' }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container direction='column' spacing={1}>
              <Grid item>
                <MDTypography variant='h6' color='dark' mb={0.5}>
                  {chartType === 'KPI'
                    ? 'KPI Long Description (only shown in downloaded Report)'
                    : '  Chart Long Description (only shown in downloaded Report)'}
                </MDTypography>
                <MDInput
                  name='chart_long_desc'
                  placeholder={
                    chartType === 'KPI'
                      ? 'Give a long description to your KPI eg: The total expected total number of visitors was 1000 for the month of June. 80% of the goal was reached.'
                      : 'Give a long description to your chart e.g. This spike in sales for AirPods in quarter 4 is mostly for last season’s AirPod Pro 2. With the launch of new AirPods Pro 3 by Apple in Sept, the price of Pro 2 went down.'
                  }
                  fullWidth
                  multiline
                  rows={3}
                  value={chartDetails.chart_long_desc}
                  onChange={handleChange}
                  error={errorMessage?.chart_long_desc.length !== 0}
                  helperText={errorMessage?.chart_long_desc}
                  sx={{ backgroundImage: 'none' }}
                  inputProps={{ style: { height: '102px' } }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ mr: 3 }}>
        <MDButton
          size='medium'
          color='primary'
          variant='gradient'
          onClick={handleUpdateChart}
        >
          Make Changes
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChartDetailsModal;
